import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  TenantVisualProfile,
  LicensingTier,
  ModuleId,
  TenantBootstrapResponse,
  SANDBOX_PROFILE,
  MODULE_TIER_MAP,
  BusinessProfile,
  WhiteLabelConfig,
  SubscriptionPlan,
  DEFAULT_BUSINESS_PROFILE,
  DEFAULT_WHITE_LABEL,
} from '../core/types/SystemConfig';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { tierAtLeast } from '../../supabase/functions/_shared/planFeatures';

interface TenantContextType extends TenantVisualProfile {
  masterTenantId: string;
  isLoaded: boolean;
  isWhiteLabeled: boolean;
  detectSubdomainBranding: () => Promise<void>;
  verifyModuleAccess: (moduleId: ModuleId) => boolean;
  updateTenantContext: (updates: Partial<TenantVisualProfile>) => void;
  refreshBusinessProfile: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function normalizeBootstrapResponse(data: TenantBootstrapResponse): TenantVisualProfile {
  return {
    tenantId: data.tenantId,
    brandName: data.brandName,
    primaryColorHex: data.primaryColorHex || '#f59e0b',
    logoAssetPath: data.logoAssetPath || '/logo.svg',
    customDomain: data.customDomain || 'localhost',
    licensingTier: data.licenseTier,
    allowedModules: data.allowedModules?.length
      ? data.allowedModules
      : MODULE_TIER_MAP[data.licenseTier] || MODULE_TIER_MAP.SOLO_PRO,
  };
}

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tenantState, setTenantState] = useState<TenantVisualProfile>(SANDBOX_PROFILE);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isWhiteLabeled, setIsWhiteLabeled] = useState(false);

  const detectSubdomainBranding = async () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Only attempt subdomain detection for multi-part hostnames
    // (e.g. agency1.app.bolt.host or agency1.aurelianmarketing.com)
    // Skip localhost and bare bolt.host / app domains with no subdomain prefix.
    if (parts.length < 3) return;

    const subdomain = parts[0].toLowerCase();

    // Skip common non-branded prefixes
    const skipPrefixes = ['www', 'app', 'api'];
    if (skipPrefixes.includes(subdomain)) return;

    try {
      const { data } = await supabase
        .from('white_label_config')
        .select('brand_name, logo_url, primary_color, secondary_color, custom_domain, branded_subdomain, hide_powered_by')
        .eq('branded_subdomain', subdomain)
        .maybeSingle();

      if (data) {
        setTenantState(prev => ({
          ...prev,
          brandName: data.brand_name || prev.brandName,
          primaryColorHex: data.primary_color || prev.primaryColorHex,
          logoAssetPath: data.logo_url || prev.logoAssetPath,
          customDomain: hostname,
        }));
        setIsWhiteLabeled(true);
      }
    } catch {
      // Subdomain lookup failed — fall back to default branding
    }
  };

  const refreshBusinessProfile = async () => {
    if (!user) return;
    const [bpRes, wlRes, subRes] = await Promise.all([
      supabase.from('business_profiles').select('*').eq('user_id', user.id).maybeSingle(),
      supabase.from('white_label_config').select('*').eq('user_id', user.id).maybeSingle(),
      supabase.from('user_subscriptions').select('*, subscription_plans(*)').eq('user_id', user.id).maybeSingle(),
    ]);

    const businessProfile: BusinessProfile = bpRes.data ? {
      businessName: bpRes.data.business_name || '',
      tagline: bpRes.data.tagline || '',
      industry: bpRes.data.industry || '',
      websiteUrl: bpRes.data.website_url || '',
      contactEmail: bpRes.data.contact_email || '',
      contactPhone: bpRes.data.contact_phone || '',
      businessAddress: bpRes.data.business_address || '',
      brandColorPrimary: bpRes.data.brand_color_primary || '#f59e0b',
      brandColorSecondary: bpRes.data.brand_color_secondary || '#3b82f6',
      logoUrl: bpRes.data.logo_url || '',
      businessDescription: bpRes.data.business_description || '',
      targetAudience: bpRes.data.target_audience || '',
      uniqueSellingProposition: bpRes.data.unique_selling_proposition || '',
      socialLinks: bpRes.data.social_links || {},
    } : DEFAULT_BUSINESS_PROFILE;

    const hasWhiteLabel = tierAtLeast(subRes.data?.subscription_plans?.tier_key, 'AGENCY');
    if (!hasWhiteLabel) setIsWhiteLabeled(false);

    const whiteLabelConfig: WhiteLabelConfig = wlRes.data && hasWhiteLabel ? {
      brandName: wlRes.data.brand_name || 'Aurelian Marketing Engine',
      logoUrl: wlRes.data.logo_url || '',
      primaryColor: wlRes.data.primary_color || '#f59e0b',
      secondaryColor: wlRes.data.secondary_color || '#3b82f6',
      customDomain: wlRes.data.custom_domain || '',
      customEmailFromName: wlRes.data.custom_email_from_name || '',
      hidePoweredBy: wlRes.data.hide_powered_by || false,
    } : DEFAULT_WHITE_LABEL;

    const subscriptionPlan: SubscriptionPlan | undefined = subRes.data?.subscription_plans ? {
      id: subRes.data.subscription_plans.id,
      name: subRes.data.subscription_plans.name,
      tierKey: subRes.data.subscription_plans.tier_key,
      priceMonthly: subRes.data.subscription_plans.price_monthly,
      priceAnnually: subRes.data.subscription_plans.price_annually,
      maxSequences: subRes.data.subscription_plans.max_sequences,
      maxEmailsPerMonth: subRes.data.subscription_plans.max_emails_per_month,
      maxImagesPerMonth: subRes.data.subscription_plans.max_images_per_month,
      maxScheduledPosts: subRes.data.subscription_plans.max_scheduled_posts,
      maxAffiliateLinks: subRes.data.subscription_plans.max_affiliate_links,
      features: subRes.data.subscription_plans.features || [],
      isActive: subRes.data.subscription_plans.is_active,
      sortOrder: subRes.data.subscription_plans.sort_order,
    } : undefined;

    setTenantState(prev => ({
      ...prev,
      businessProfile,
      whiteLabelConfig,
      subscriptionPlan,
      brandName: whiteLabelConfig.brandName || prev.brandName,
      primaryColorHex: whiteLabelConfig.primaryColor || prev.primaryColorHex,
    }));
  };

  useEffect(() => {
    const bootstrapTenantProfile = async () => {
      // First: detect subdomain-based white-label branding (runs before edge function)
      await detectSubdomainBranding();

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const response = await fetch(`${supabaseUrl}/functions/v1/tenant-profile`, {
          headers: {
            Authorization: `Bearer ${anonKey}`,
            apikey: anonKey,
          },
        });
        if (response.ok) {
          const profileData: TenantBootstrapResponse = await response.json();
          // Only override subdomain branding if no white-label was detected
          if (!isWhiteLabeled) {
            setTenantState(normalizeBootstrapResponse(profileData));
          }
        }
      } catch {
        // Edge route unreachable — fall back to sandbox profile
      } finally {
        setIsLoaded(true);
      }
    };
    bootstrapTenantProfile();
  }, []);

  useEffect(() => {
    if (user) {
      refreshBusinessProfile();
    }
  }, [user]);

  const verifyModuleAccess = (moduleId: ModuleId): boolean => {
    if (tenantState.licensingTier === 'SYSTEM_OWNER') return true;
    return tenantState.allowedModules.includes(moduleId);
  };

  const updateTenantContext = (updates: Partial<TenantVisualProfile>) => {
    setTenantState(prev => ({ ...prev, ...updates }));
  };

  return (
    <TenantContext.Provider value={{ ...tenantState, tenantId: user?.id || tenantState.tenantId, masterTenantId: tenantState.tenantId, isLoaded, isWhiteLabeled, detectSubdomainBranding, verifyModuleAccess, updateTenantContext, refreshBusinessProfile }}>
      {!isLoaded ? (
        <div className="flex min-h-screen items-center justify-center bg-slate-100 font-mono text-xs text-primary-600">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          Synchronizing Multi-Tenant Vault Context...
        </div>
      ) : (
        children
      )}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    return {
      ...SANDBOX_PROFILE,
      masterTenantId: SANDBOX_PROFILE.tenantId,
      isLoaded: true,
      isWhiteLabeled: false,
      detectSubdomainBranding: async () => {},
      verifyModuleAccess: () => true,
      updateTenantContext: () => {},
      refreshBusinessProfile: async () => {},
    };
  }
  return context;
};
